import { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useStore } from '@tanstack/react-form';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { PageHeader } from '@/common/components/page-header/PageHeader';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { deviceQueries, deviceQueryKeys } from '@/features/devices/queries';
import { DeviceService } from '@/features/devices/services/DeviceService';
import { PARENT_NONE, makeDeviceFormSchema } from '@/features/devices/config';
import { ParameterField } from '@/features/devices/components/parameter-field/ParameterField';
import { useDeviceParameters } from '@/features/devices/hooks/useDeviceParameters';

export function CreateDevicePage() {
  const { activeSupplyPoint } = useSupplyPoint();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: devices } = useSuspenseQuery(
    deviceQueries.listByEnergySystem(activeSupplyPoint!.id)
  );
  const { data: deviceTypes } = useSuspenseQuery(deviceQueries.types());

  const [createdDeviceId, setCreatedDeviceId] = useState<number | null>(null);
  const [createdTypeId, setCreatedTypeId] = useState<number | null>(null);

  const parameterDefinitions =
    deviceTypes
      .find((t) => t.id === createdTypeId)
      ?.parameterdefinitionSet.filter((d) => d.role === 'parameter') ?? [];

  const params = useDeviceParameters(parameterDefinitions, {
    deviceId: createdDeviceId,
    onSuccess: () => navigate({ to: '/devices' }),
  });

  const formSchema = useMemo(() => makeDeviceFormSchema(deviceTypes), [deviceTypes]);

  const form = useAppForm(
    appFormOptions({
      schema: formSchema,
      defaultValues: {
        name: null,
        deviceType: null,
        parent: PARENT_NONE,
      },
      onSubmit: async ({ value }) => {
        const selectedType = deviceTypes.find((t) => t.id === value.deviceType);
        const requiresParent = (selectedType?.deviceGroup.parent ?? null) !== null;

        const newDevice = await DeviceService.create({
          name: value.name,
          deviceType: value.deviceType,
          energySystem: activeSupplyPoint!.id,
          parent: requiresParent ? value.parent : null,
        });

        await queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });

        const definitions =
          deviceTypes
            .find((t) => t.id === value.deviceType)
            ?.parameterdefinitionSet.filter((d) => d.role === 'parameter') ?? [];

        if (definitions.length === 0) {
          navigate({ to: '/devices' });
          return;
        }

        const defaults = Object.fromEntries(definitions.map((def) => [def.id, def.value ?? '']));
        params.reset(defaults);
        setCreatedTypeId(value.deviceType);
        setCreatedDeviceId(newDevice.id);
      },
    })
  );

  const selectedDeviceTypeId = useStore(form.store, (s) => s.values.deviceType);
  const selectedDeviceType = deviceTypes.find((t) => t.id === Number(selectedDeviceTypeId));
  const parentGroupId = selectedDeviceType?.deviceGroup.parent ?? null;
  const isParentRequired = parentGroupId !== null;

  const parentGroupTypeIds =
    parentGroupId !== null
      ? deviceTypes.filter((t) => t.deviceGroup.id === parentGroupId).map((t) => t.id)
      : [];
  const availableParents =
    parentGroupId !== null ? devices.filter((d) => parentGroupTypeIds.includes(d.deviceType)) : [];

  const isStep2 = createdDeviceId !== null;

  return (
    <div className="flex flex-col gap-6 max-w-3xl pb-10">
      <PageHeader
        title={<Trans>Add new device</Trans>}
        subtitle={<Trans>Configure the device parameters and connection.</Trans>}
      />

      {/* Step 1: Basic info */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          {isStep2 ? (
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
              ✓
            </span>
          ) : (
            <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-primary text-primary text-xs font-bold shrink-0">
              1
            </span>
          )}
          <div>
            <h2 className="text-base font-semibold">
              <Trans>Basic configuration</Trans>
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              <Trans>General information about this device.</Trans>
            </p>
          </div>
        </div>

        {!isStep2 && (
          <form.AppForm>
            <form.FormElement>
              <div className="px-6 py-6 flex flex-col gap-6">
                <FormErrors />
                <div className="grid gap-4 md:grid-cols-2">
                  <form.AppField name="name">
                    {(field) => (
                      <field.TextField
                        label={<Trans>Device name</Trans>}
                        placeholder={t`e.g. Solar Inverter A`}
                        required
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="deviceType">
                    {(field) => (
                      <field.SelectField
                        label={<Trans>Device type</Trans>}
                        required
                        placeholder={t`Select type`}
                      >
                        {deviceTypes.map((type) => (
                          <SelectItem key={type.id} value={String(type.id)}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>

                  {isParentRequired && (
                    <div className="flex flex-col gap-1">
                      <form.AppField name="parent">
                        {(field) => (
                          <field.SelectField
                            label={<Trans>Parent device</Trans>}
                            required
                            placeholder={t`Select parent`}
                            disabled={availableParents.length === 0}
                          >
                            {availableParents.map((d) => {
                              const typeName =
                                deviceTypes.find((t) => t.id === d.deviceType)?.name ?? '';
                              return (
                                <SelectItem key={d.id} value={String(d.id)}>
                                  {d.name} ({typeName})
                                </SelectItem>
                              );
                            })}
                          </field.SelectField>
                        )}
                      </form.AppField>
                      {availableParents.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          <Trans>
                            No devices available in the required group. Add a parent device first.
                          </Trans>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate({ to: '/devices' })}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <FormSubmit>
                    <Trans>Create device</Trans>
                  </FormSubmit>
                </div>
              </div>
            </form.FormElement>
          </form.AppForm>
        )}
      </div>

      {/* Step 2: Parameters */}
      {isStep2 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-3">
            <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-primary text-primary text-xs font-bold shrink-0">
              2
            </span>
            <div>
              <h2 className="text-base font-semibold">
                <Trans>Parameters</Trans>
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                <Trans>Configure the parameters for this device.</Trans>
              </p>
            </div>
          </div>
          <div className="px-6 py-6 flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              {parameterDefinitions.map((def) => (
                <ParameterField key={def.id} definition={def} {...params.getFieldProps(def)} />
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate({ to: '/devices' })}>
                <Trans>Skip</Trans>
              </Button>
              <Button type="button" onClick={() => void params.save()} disabled={params.isSaving}>
                <Trans>Save & finish</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
